import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { EventEmitter } from '~/lib/helper/EventHelper';

interface LiveRegionProps {
    /** Messages fired here are announced politely. Subscribed once, on mount. */
    source: EventEmitter<string>;
}

// Visually-hidden polite screen-reader announcer for the messages fired on `source`.
//
// Uses the two-region alternation technique (as in @react-aria/live-announcer):
// each message is written to whichever region is currently idle while the other
// is cleared. Because every announcement is a genuine "" -> text change on a
// fresh node, assistive technology re-reads it even when the text is identical
// to the previous one (e.g. reloading the same list). A single region would not:
// MobX/React/the browser all skip an unchanged value, so a repeat would be silent.
//
// Messages are cleared a few seconds after firing, so a stale one isn't met again
// in browse mode beside whatever content the region sits next to.
@observer
export default class LiveRegion extends React.Component<LiveRegionProps> {
    /** Long enough for AT to finish speech queued ahead of a polite message (react-aria's default). */
    static readonly clearAfterMs = 7000;

    @observable
    messageA = '';

    @observable
    messageB = '';

    private writeToA = true;

    private disposer?: () => unknown = undefined;

    private clearTimer?: ReturnType<typeof setTimeout> = undefined;

    constructor(props: LiveRegionProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.disposer = this.props.source.on((message) => {
            runInAction(() => {
                if (this.writeToA) {
                    this.messageA = message;
                    this.messageB = '';
                } else {
                    this.messageB = message;
                    this.messageA = '';
                }
                this.writeToA = !this.writeToA;
            });
            clearTimeout(this.clearTimer);
            this.clearTimer = setTimeout(() => {
                runInAction(() => {
                    this.messageA = '';
                    this.messageB = '';
                });
            }, LiveRegion.clearAfterMs);
        });
    }

    componentWillUnmount() {
        this.disposer?.();
        clearTimeout(this.clearTimer);
    }

    render() {
        return (
            <>
                <span className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
                    {this.messageA}
                </span>
                <span className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
                    {this.messageB}
                </span>
            </>
        );
    }
}
